import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, FileSpreadsheet, Check, Loader2, BarChart3, Database,
  Sparkles, ArrowRight, AlertCircle, ChevronRight, Eye, MessageSquare,
  ChevronLeft, Lightbulb, FileText, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { getRegisteredVizTypes } from '@/lib/customVizRegistry';
import { apiFetch } from '@/lib/api';
import { useBrandName } from '@/lib/branding';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'upload', label: 'Upload', icon: Upload },
  { id: 'context', label: 'Tell Us More', icon: MessageSquare },
  { id: 'processing', label: 'AI Building', icon: Sparkles },
  { id: 'complete', label: 'Ready', icon: Check },
];

const PROCESSING_MSGS = [
  'Importing with correct data types...',
  'Building semantic model...',
  'Creating calculated measures...',
  'AI evaluating and improving model...',
  'Generating executive summary...',
  'Building trend analysis...',
  'Creating detailed breakdown...',
  'Finalizing dashboards...',
];

export default function QuickstartPage() {
  const navigate = useNavigate();
  const APP = useBrandName();
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  // Snapshot the plugin-registered viz types once per page mount. Cheap today (registry is empty),
  // but this avoids recomputing the list on every keystroke or filter change once plugins ship.
  const registeredVizTypes = useMemo(() => getRegisteredVizTypes().map(reg => ({
    type: reg.type,
    label: reg.label || reg.type,
    description: reg.meta?.description || '',
  })), []);

  const [step, setStep] = useState('upload');
  const [sessionId, setSessionId] = useState(null);
  const [fileName, setFileName] = useState('');
  const [rowCount, setRowCount] = useState(0);
  const [colCount, setColCount] = useState(0);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [modelResult, setModelResult] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [processingMsg, setProcessingMsg] = useState(0);
  const [error, setError] = useState(null);

  // PDF review (extracted table awaiting user confirmation)
  const [pdfTable, setPdfTable] = useState(null);   // { headers: [], rows: [[]] }
  const [pdfNote, setPdfNote] = useState('');
  const [confirming, setConfirming] = useState(false);

  // User context form
  const [bizDesc, setBizDesc] = useState('');
  const [keyQuestions, setKeyQuestions] = useState('');
  const [timePeriod, setTimePeriod] = useState('');
  const [industry, setIndustry] = useState('');
  const [kpis, setKpis] = useState('');
  const uploadingRef = useRef(false);

  // Safely read a Response body exactly once — avoids "body stream already read"
  const readResponse = async (res) => {
    const text = await res.text();
    try { return { ok: res.ok, data: text ? JSON.parse(text) : {}, raw: text }; }
    catch { return { ok: res.ok, data: null, raw: text }; }
  };

  const handleDrag = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); }, []);
  const handleDrop = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); if (e.dataTransfer.files?.[0]) handleUpload(e.dataTransfer.files[0]); }, []);

  // Turn a failed response into a user-friendly message. Gateway timeouts
  // (502/503/504) and proxy error pages return raw HTML — never surface that.
  const friendlyError = ({ data, raw, status }, fallback) => {
    if (data && data.detail) return data.detail;
    const looksHtml = typeof raw === 'string' && /<\s*(?:!doctype|html)/i.test(raw.trim().slice(0, 200));
    if ([502, 503, 504].includes(status) || looksHtml) {
      return 'The AI step took longer than expected and the request timed out. Your data is saved — please click Build again to retry.';
    }
    return (raw && raw.slice(0, 300)) || fallback || `Request failed (HTTP ${status || ''})`;
  };

  // Step 1: Upload + AI analysis
  const handleUpload = async (file) => {
    if (uploadingRef.current) return;  // guard against double-fire (drop + click / strict mode)
    const lower = file.name.toLowerCase();
    const isPdf = lower.endsWith('.pdf');
    if (!/\.(csv|xlsx|xlsm|xls|pdf)$/.test(lower)) { toast.error('Please upload a CSV, Excel or PDF file'); return; }
    if (file.size > 50 * 1024 * 1024) { toast.error('File too large. Max 50MB.'); return; }

    uploadingRef.current = true;
    setError(null);
    setFileName(file.name);
    setStep('processing');
    setProcessingMsg(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      if (isPdf) {
        // PDF path: extract a table with AI, then let the user review/confirm it.
        const { ok, data, raw, status } = await apiFetch(`${API}/api/quickstart/upload-pdf`, { method: 'POST', body: formData });
        if (!ok) throw new Error(friendlyError({ data, raw, status }, 'PDF extraction failed'));
        setSessionId(data.session_id);
        setPdfTable(data.extracted || { headers: [], rows: [] });
        setPdfNote('');
        setStep('pdfReview');
        return;
      }

      const { ok, data, raw, status } = await apiFetch(`${API}/api/quickstart/upload-and-analyze`, { method: 'POST', body: formData });
      if (!ok) throw new Error(friendlyError({ data, raw, status }, 'Upload failed'));

      setSessionId(data.session_id);
      setRowCount(data.row_count);
      setColCount(data.column_count);
      setAiAnalysis(data.ai_analysis);

      // Note: AI-suggested questions are still in `data.ai_analysis.questions_for_user`
      // and shown as a hint banner on the next step. We intentionally do NOT
      // prefill the textarea — users prefer it blank to write their own questions
      // without clearing AI text first.

      setStep('context');
    } catch (err) {
      setError(err.message);
      setStep('upload');
      toast.error(err.message);
    } finally {
      uploadingRef.current = false;
      // Reset the file input so the same file can be picked again
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // PDF review helpers — edit the extracted table in place
  const updatePdfCell = (ri, ci, value) => {
    setPdfTable(prev => {
      const rows = prev.rows.map(r => [...r]);
      rows[ri][ci] = value;
      return { ...prev, rows };
    });
  };
  const updatePdfHeader = (ci, value) => {
    setPdfTable(prev => ({ ...prev, headers: prev.headers.map((h, i) => i === ci ? value : h) }));
  };
  const deletePdfRow = (ri) => {
    setPdfTable(prev => ({ ...prev, rows: prev.rows.filter((_, i) => i !== ri) }));
  };

  // Confirm the reviewed table → run the same AI analysis as CSV/Excel
  const confirmPdf = async () => {
    if (!pdfTable || !pdfTable.headers.length || !pdfTable.rows.length) {
      toast.error('The table needs at least one column and one row.');
      return;
    }
    setConfirming(true);
    setError(null);
    try {
      const { ok, data, raw, status } = await apiFetch(`${API}/api/quickstart/${sessionId}/confirm-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ headers: pdfTable.headers, rows: pdfTable.rows, context_note: pdfNote }),
      });
      if (!ok) throw new Error(friendlyError({ data, raw, status }, 'Could not confirm the data'));
      setRowCount(data.row_count);
      setColCount(data.column_count);
      setAiAnalysis(data.ai_analysis);
      setStep('context');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setConfirming(false);
    }
  };

  // Step 2 → 3: Submit context and run full pipeline
  const runPipeline = async () => {
    setStep('processing');
    setProcessingMsg(0);
    let msgIdx = 0;
    const msgTimer = setInterval(() => { msgIdx = Math.min(msgIdx + 1, PROCESSING_MSGS.length - 1); setProcessingMsg(msgIdx); }, 3000);

    try {
      // Import with context
      setProcessingMsg(0);
      const importRes = await apiFetch(`${API}/api/quickstart/${sessionId}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_description: bizDesc,
          key_questions: keyQuestions.split('\n').filter(Boolean),
          time_period: timePeriod,
          industry,
          kpis,
        }),
      });
      if (!importRes.ok) throw new Error(friendlyError(importRes, 'Import failed'));

      // Generate model
      setProcessingMsg(1);
      const modelParsed = await apiFetch(`${API}/api/quickstart/${sessionId}/generate-model`, { method: 'POST' });
      if (!modelParsed.ok) throw new Error(friendlyError(modelParsed, 'Model generation failed'));
      setModelResult(modelParsed.data);
      setProcessingMsg(4);

      // Generate dashboards
      const extraVizTypes = registeredVizTypes;
      const dashParsed = await apiFetch(`${API}/api/quickstart/${sessionId}/generate-dashboards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ extra_viz_types: extraVizTypes }),
      });
      if (!dashParsed.ok) throw new Error(friendlyError(dashParsed, 'Dashboard generation failed'));
      setDashboards(dashParsed.data.dashboards || []);

      clearInterval(msgTimer);
      setStep('complete');
      toast.success('Your analytics are ready!');
    } catch (err) {
      clearInterval(msgTimer);
      setError(err.message);
      setStep('context');
      toast.error(err.message);
    }
  };

  // 'pdfReview' is a sub-stage of Upload — keep the stepper on the first step.
  const currentStepIdx = step === 'pdfReview' ? 0 : STEPS.findIndex(s => s.id === step);

  return (
    <div className="min-h-[calc(100vh-60px)] bg-background" data-testid="quickstart-page">
      {/* Step indicator */}
      <div className="border-b border-border/40 bg-muted/20">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === currentStepIdx;
              const isDone = i < currentStepIdx;
              return (
                <React.Fragment key={s.id}>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${isDone ? 'bg-primary text-primary-foreground' : isActive ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-muted text-muted-foreground'}`}>
                      {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className={`text-sm hidden sm:inline ${isActive ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-muted-foreground/30 mx-2" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <AnimatePresence mode="wait">
          {/* ── UPLOAD ── */}
          {step === 'upload' && (
            <motion.div key="upload" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-semibold mb-2" data-testid="quickstart-title">Upload your data</h1>
                <p className="text-muted-foreground">Drop a CSV, Excel or PDF file and {APP} will automatically analyze it, build your data model, and generate dashboards.</p>
              </div>
              <div
                className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer ${dragActive ? 'border-primary bg-primary/5' : 'border-border/50 hover:border-primary/50 hover:bg-muted/20'}`}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                data-testid="quickstart-dropzone"
              >
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xlsm,.xls,.pdf" onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])} className="hidden" />
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4"><Upload className="w-8 h-8 text-primary" /></div>
                <p className="text-lg font-medium mb-1">Drag & drop your CSV, Excel or PDF file here</p>
                <p className="text-sm text-muted-foreground mb-4">or click to browse</p>
                <div className="flex items-center justify-center gap-2"><FileSpreadsheet className="w-4 h-4 text-muted-foreground" /><span className="text-xs text-muted-foreground">CSV, XLSX, XLS or PDF files up to 50MB</span></div>
              </div>
              {error && <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20"><AlertCircle className="w-4 h-4 text-destructive" /><span className="text-sm text-destructive">{error}</span></div>}
            </motion.div>
          )}

          {/* ── PDF REVIEW: confirm AI-extracted table ── */}
          {step === 'pdfReview' && pdfTable && (
            <motion.div key="pdfReview" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} data-testid="quickstart-pdf-review">
              <div className="mb-6">
                <Button variant="ghost" size="sm" onClick={() => { setStep('upload'); setPdfTable(null); }} className="gap-1 text-muted-foreground mb-4"><ChevronLeft className="w-3.5 h-3.5" /> Back</Button>
                <h1 className="text-2xl font-semibold mb-2 flex items-center gap-2"><FileText className="w-6 h-6 text-primary" /> Review extracted data</h1>
                <p className="text-muted-foreground">{APP} read <span className="font-medium">{fileName}</span> and pulled out this table. Check the values, fix anything that looks off, then confirm to continue.</p>
              </div>

              <div className="rounded-xl border border-border/60 bg-muted/10 p-3 mb-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Lightbulb className="w-4 h-4 text-amber-500 shrink-0" />
                AI extraction can make mistakes. Edit any cell or header directly, and remove rows that don't belong (like totals or page footers).
              </div>

              <div className="overflow-auto max-h-[52vh] rounded-xl border border-border/60" data-testid="quickstart-pdf-table">
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 bg-background z-10">
                    <tr>
                      <th className="w-10 border-b border-border/60 p-2 text-xs text-muted-foreground font-medium">#</th>
                      {pdfTable.headers.map((h, ci) => (
                        <th key={ci} className="border-b border-l border-border/60 p-1 min-w-[120px]">
                          <Input value={h} onChange={e => updatePdfHeader(ci, e.target.value)} className="h-8 font-medium" data-testid={`pdf-header-${ci}`} />
                        </th>
                      ))}
                      <th className="w-10 border-b border-l border-border/60" />
                    </tr>
                  </thead>
                  <tbody>
                    {pdfTable.rows.map((row, ri) => (
                      <tr key={ri} className="hover:bg-muted/20">
                        <td className="border-b border-border/40 p-2 text-center text-xs text-muted-foreground">{ri + 1}</td>
                        {pdfTable.headers.map((_, ci) => (
                          <td key={ci} className="border-b border-l border-border/40 p-1">
                            <Input value={row[ci] ?? ''} onChange={e => updatePdfCell(ri, ci, e.target.value)} className="h-8" data-testid={`pdf-cell-${ri}-${ci}`} />
                          </td>
                        ))}
                        <td className="border-b border-l border-border/40 p-1 text-center">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => deletePdfRow(ri)} data-testid={`pdf-delete-row-${ri}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{pdfTable.rows.length} rows · {pdfTable.headers.length} columns</p>

              <div className="mt-6">
                <label className="text-sm font-medium mb-2 block">Anything {APP} should know about this data? <span className="text-muted-foreground font-normal">(optional)</span></label>
                <Textarea
                  value={pdfNote}
                  onChange={e => setPdfNote(e.target.value)}
                  placeholder="e.g. These are monthly regional sales figures for 2024. Revenue is in USD thousands."
                  className="min-h-[80px]"
                  data-testid="pdf-context-note"
                />
              </div>

              {error && <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20"><AlertCircle className="w-4 h-4 text-destructive" /><span className="text-sm text-destructive">{error}</span></div>}

              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => { setStep('upload'); setPdfTable(null); }} disabled={confirming}>Cancel</Button>
                <Button onClick={confirmPdf} disabled={confirming} className="gap-2" data-testid="pdf-confirm-btn">
                  {confirming ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</> : <>Looks good — continue <ArrowRight className="w-4 h-4" /></>}
                </Button>
              </div>
            </motion.div>
          )}


          {/* ── CONTEXT: Tell Us More ── */}
          {step === 'context' && (
            <motion.div key="context" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
              <div className="mb-6">
                <Button variant="ghost" size="sm" onClick={() => setStep('upload')} className="gap-1 text-muted-foreground mb-4"><ChevronLeft className="w-3.5 h-3.5" /> Back</Button>
                <h1 className="text-2xl font-semibold mb-2" data-testid="context-title">Tell us about your data</h1>
                <p className="text-muted-foreground">This helps {APP} build more meaningful models and dashboards.</p>
              </div>

              {/* AI Summary */}
              {aiAnalysis && (
                <div className="p-4 rounded-xl border border-primary/20 bg-primary/[0.04] mb-6" data-testid="ai-summary">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium mb-1">AI detected</p>
                      <p className="text-xs text-muted-foreground">{aiAnalysis.dataset_summary}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <Badge variant="outline" className="text-[10px]"><FileSpreadsheet className="w-3 h-3 mr-1" /> {fileName}</Badge>
                        <Badge variant="outline" className="text-[10px]">{rowCount.toLocaleString()} rows</Badge>
                        <Badge variant="outline" className="text-[10px]">{colCount} columns</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI suggested questions */}
              {aiAnalysis?.questions_for_user?.length > 0 && (
                <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-amber-500/[0.06] border border-amber-500/15">
                  <Lightbulb className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground">AI suggested these questions — your answers will improve the dashboards:</p>
                </div>
              )}

              {/* User form */}
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">What does your business do? <span className="text-muted-foreground/70">(optional)</span></label>
                  <Input value={bizDesc} onChange={e => setBizDesc(e.target.value)} placeholder="e.g., We sell specialty insurance products across North America" className="text-foreground" data-testid="ctx-business" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">What industry are you in? <span className="text-muted-foreground/70">(optional)</span></label>
                  <Input value={industry} onChange={e => setIndustry(e.target.value)} placeholder="e.g., Insurance, Retail, Healthcare, SaaS" className="text-foreground" data-testid="ctx-industry" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">What questions do you want answered?</label>
                  <Textarea value={keyQuestions} onChange={e => setKeyQuestions(e.target.value)} rows={3} placeholder="e.g., What are our most profitable products? How are claims trending? Which regions are growing fastest?" className="text-sm resize-none text-foreground" data-testid="ctx-questions" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Time period covered <span className="text-muted-foreground/70">(optional)</span></label>
                    <Input value={timePeriod} onChange={e => setTimePeriod(e.target.value)} placeholder="e.g., 2020-2025, Last 3 years" className="text-foreground" data-testid="ctx-time" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Key KPIs you track <span className="text-muted-foreground/70">(optional)</span></label>
                    <Input value={kpis} onChange={e => setKpis(e.target.value)} placeholder="e.g., Loss ratio, Combined ratio, Revenue growth" className="text-foreground" data-testid="ctx-kpis" />
                  </div>
                </div>
              </div>

              {error && <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20"><AlertCircle className="w-4 h-4 text-destructive" /><span className="text-sm text-destructive">{error}</span></div>}

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => { setKeyQuestions(''); setBizDesc(''); setIndustry(''); setTimePeriod(''); setKpis(''); runPipeline(); }}>Skip &amp; Build</Button>
                <Button onClick={runPipeline} className="gap-2" data-testid="build-btn"><Sparkles className="w-4 h-4" /> Build My Analytics</Button>
              </div>
            </motion.div>
          )}

          {/* ── PROCESSING ── */}
          {step === 'processing' && (
            <motion.div key="processing" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} className="text-center py-16">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6 relative">
                <Sparkles className="w-9 h-9 text-primary" />
                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 3, ease: 'linear' }} className="absolute inset-0 rounded-2xl border-2 border-primary/20 border-t-primary" />
              </div>
              <h2 className="text-xl font-semibold mb-2">AI is building your analytics</h2>
              <motion.p key={processingMsg} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="text-muted-foreground mb-8">{PROCESSING_MSGS[processingMsg]}</motion.p>
              <div className="flex justify-center gap-1">
                {[0,1,2,3,4,5,6,7].map(i => (
                  <motion.div key={i} animate={{ height: [6, 22, 6], opacity: [0.2, 0.8, 0.2] }} transition={{ repeat: Infinity, duration: 0.7, delay: i * 0.08 }} className="w-1 rounded-full bg-primary" style={{ height: 6 }} />
                ))}
              </div>
            </motion.div>
          )}

          {/* ── COMPLETE ── */}
          {step === 'complete' && (
            <motion.div key="complete" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7 text-emerald-500" /></div>
                <h2 className="text-xl font-semibold mb-1" data-testid="complete-title">Your analytics are ready</h2>
                <p className="text-muted-foreground">{APP} analyzed your data, built a model, and generated {dashboards.length} dashboard{dashboards.length > 1 ? 's' : ''}.</p>
              </div>

              {/* Model summary */}
              {modelResult && (
                <div className="p-5 rounded-xl border border-border/50 bg-card mb-4">
                  <div className="flex items-center gap-2 mb-3"><Database className="w-4 h-4 text-primary" /><span className="text-sm font-medium">Semantic Model</span>
                    {modelResult.evaluation?.quality_score && <Badge variant="outline" className="text-[9px] ml-auto">{modelResult.evaluation.quality_score}% quality</Badge>}
                  </div>
                  <p className="text-base font-semibold mb-1">{modelResult.model?.name}</p>
                  <p className="text-xs text-muted-foreground mb-3">{modelResult.model?.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {modelResult.entity?.attributes?.slice(0, 6).map(a => (
                      <span key={a.name} className={`text-[9px] px-2 py-0.5 rounded-full border ${a.role === 'measure' || a.semantic_type === 'measure' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : a.role === 'time_dimension' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
                        {a.display_name || a.name}
                      </span>
                    ))}
                    {(modelResult.entity?.attributes?.length || 0) > 6 && <span className="text-[9px] text-muted-foreground">+{modelResult.entity.attributes.length - 6} more</span>}
                  </div>
                  {modelResult.calculated_measures?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/30">
                      <p className="text-[10px] text-muted-foreground mb-1.5">Calculated Measures</p>
                      <div className="flex flex-wrap gap-2">
                        {modelResult.calculated_measures.map(cm => (
                          <span key={cm.name} className="text-[9px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20" title={cm.description}>{cm.display_name || cm.name}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* AI plugin viz callout — surfaces when the AI chose a custom-registered viz type */}
              {(() => {
                const allPluginTypes = [];
                const seenTypes = new Set();
                dashboards.forEach(d => (d.custom_viz_types || []).forEach(t => {
                  if (!seenTypes.has(t.type)) { seenTypes.add(t.type); allPluginTypes.push(t); }
                }));
                if (allPluginTypes.length === 0) return null;
                return (
                  <div className="mb-4 p-3 rounded-xl border border-violet-500/30 bg-violet-500/5 flex items-start gap-3" data-testid="ai-plugin-callout">
                    <Sparkles className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-violet-200">
                        AI picked {allPluginTypes.length} custom viz type{allPluginTypes.length > 1 ? 's' : ''} from your plugin library
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        These chart types were registered by your team — Quickstart noticed them and used them where appropriate.
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {allPluginTypes.map(t => (
                          <span key={t.type} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-200 border border-violet-500/30" data-testid={`ai-plugin-chip-${t.type}`}>
                            {t.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Dashboards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {dashboards.map((dash, i) => (
                  <button key={dash.report_id} onClick={() => navigate(`/dashboards/view?id=${dash.report_id}`)}
                    className="p-5 rounded-xl border border-border/50 bg-card hover:border-primary/30 hover:shadow-md transition-all text-left group" data-testid={`dash-card-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <BarChart3 className="w-4 h-4 text-primary" />
                      <Badge variant="outline" className="text-[8px]">{dash.type || 'general'}</Badge>
                      {(dash.custom_viz_types || []).length > 0 && (
                        <Badge
                          variant="outline"
                          className="text-[8px] gap-1 border-violet-500/40 bg-violet-500/10 text-violet-200"
                          title={`AI picked plugin viz: ${dash.custom_viz_types.map(t => t.label).join(', ')}`}
                          data-testid={`dash-card-${i}-ai-plugin-badge`}
                        >
                          <Sparkles className="w-2.5 h-2.5" />
                          Suggested by AI
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm font-semibold mb-1 group-hover:text-primary transition-colors">{dash.name}</p>
                    <p className="text-xs text-muted-foreground">{dash.visual_count} visuals</p>
                    {(dash.custom_viz_types || []).length > 0 && (
                      <p className="text-[10px] text-violet-300/80 mt-1" data-testid={`dash-card-${i}-ai-plugin-detail`}>
                        Includes {dash.custom_viz_types.map(t => `${t.label}${t.count > 1 ? ` ×${t.count}` : ''}`).join(', ')}
                      </p>
                    )}
                    <div className="flex items-center gap-1 mt-3 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                      Open Dashboard <ArrowRight className="w-3 h-3" />
                    </div>
                  </button>
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-center gap-3">
                {dashboards[0] && <Button onClick={() => navigate(`/dashboards/view?id=${dashboards[0].report_id}`)} className="gap-2" data-testid="view-exec-btn"><Eye className="w-4 h-4" /> View Executive Summary</Button>}
                {modelResult?.model_id && <Button variant="outline" onClick={() => navigate(`/explore?model=${modelResult.model_id}`)} className="gap-2"><Sparkles className="w-4 h-4" /> Explore with AI</Button>}
                <Button variant="ghost" onClick={() => { setStep('upload'); setError(null); setSessionId(null); }}>Upload Another</Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
